export default class CreditWorthinessFormatter {
  constructor(rawData, simulationWitness) {
    this._rawData = rawData;
    this.simulationWitness = simulationWitness;
  }

  _formatCurrency(value) {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  }

  format() {
    const cardsData = this._getCardsData();
    const moreInfoData = this._getMoreInfoData();
    const tableData = this._getTableData();
    const creditworthinessOfLoans = this._getCreditworthinessOfLoans();
    return { tableData, cardsData, moreInfoData, creditworthinessOfLoans };
  }

  _getCardsData() {
    return [
      {
        title:
          'LOANS.CREDITWORTHINESS.MONTHLY_AMORTIZATION.CARDS.MAX_MONTH_FEE.TITLE',
        subtitle:
          'LOANS.CREDITWORTHINESS.MONTHLY_AMORTIZATION.CARDS.MAX_MONTH_FEE.SUBTITLE',
        description:
          'LOANS.CREDITWORTHINESS.MONTHLY_AMORTIZATION.CARDS.MAX_MONTH_FEE.DESC',
        amount: this._rawData.maximumMonthFee,
      },
      {
        title:
          'LOANS.CREDITWORTHINESS.MONTHLY_AMORTIZATION.CARDS.YOUR_OBLIGATIONS.TITLE',
        subtitle:
          'LOANS.CREDITWORTHINESS.MONTHLY_AMORTIZATION.CARDS.YOUR_OBLIGATIONS.SUBTITLE',
        description:
          'LOANS.CREDITWORTHINESS.MONTHLY_AMORTIZATION.CARDS.YOUR_OBLIGATIONS.DESC',
        amount: this._rawData.yourObligations,
      },
      {
        title:
          'LOANS.CREDITWORTHINESS.MONTHLY_AMORTIZATION.CARDS.YOUR_REAL_CAPACITY.TITLE',
        subtitle:
          'LOANS.CREDITWORTHINESS.MONTHLY_AMORTIZATION.CARDS.YOUR_REAL_CAPACITY.SUBTITLE',
        description:
          'LOANS.CREDITWORTHINESS.MONTHLY_AMORTIZATION.CARDS.YOUR_REAL_CAPACITY.DESC',
        amount: this._rawData.yourRealCapacity,
      },
    ];
  }

  _getMoreInfoData() {
    return [
      {
        title:
          'LOANS.CREDITWORTHINESS.MORE_INFO.ABOUT_CREDITWORTHINESS.SUBTITLE',
        information: [
          'LOANS.CREDITWORTHINESS.MORE_INFO.ABOUT_CREDITWORTHINESS.DESCRIPTION1',
          'LOANS.CREDITWORTHINESS.MORE_INFO.ABOUT_CREDITWORTHINESS.DESCRIPTION2',
        ],
      },
      {
        title:
          'LOANS.CREDITWORTHINESS.MORE_INFO.FACTORS_CREDITWORTHINESS.SUBTITLE',
        information: [
          'LOANS.CREDITWORTHINESS.MORE_INFO.FACTORS_CREDITWORTHINESS.DESCRIPTION1',
          'LOANS.CREDITWORTHINESS.MORE_INFO.FACTORS_CREDITWORTHINESS.DESCRIPTION2',
        ],
      },
      {
        title: 'LOANS.CREDITWORTHINESS.MORE_INFO.LOANS_COMPATIBILITY.SUBTITLE',
        information: [
          'LOANS.CREDITWORTHINESS.MORE_INFO.LOANS_COMPATIBILITY.DESCRIPTION1',
          'LOANS.CREDITWORTHINESS.MORE_INFO.LOANS_COMPATIBILITY.DESCRIPTION2',
          'LOANS.CREDITWORTHINESS.MORE_INFO.LOANS_COMPATIBILITY.DESCRIPTION3',
          'LOANS.CREDITWORTHINESS.MORE_INFO.LOANS_COMPATIBILITY.DESCRIPTION4',
        ],
      },
    ];
  }

  _getTableData() {
    const headers = this._getTableHeaders();
    const rows = this._getTableRows();
    return { headers, rows };
  }

  _getTableHeaders() {
    return [
      {
        text: 'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.TYPE',
        width: 273,
        translate: true,
      },
      {
        text: 'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT',
        width: 198,
        translate: true,
        tooltip: 'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT_TOOLTIP',
      },
      {
        text:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT_WITHOUT_INSURANCE',
        width: 264,
        translate: true,
        tooltip:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT_WITHOUT_INSURANCE_TOOLTIP',
      },
      {
        text:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT_WITH_INSURANCE',
        width: 329,
        translate: true,
        tooltip:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT_WITH_INSURANCE_TOOLTIP',
      },
    ];
  }

  _getTableRows() {
    const rows = [];

    const {
      fiveMonth,
      nineMonth,
      variousCredit,
      socialHousingLoan,
      agreementHousingLoan,
      socialHousingLoanFixed,
      agreementHousingLoanFixed,
      diversalAtention,
    } = this._rawData;

    if (fiveMonth) {
      rows.push([
        {
          text:
            'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.WITHOUT_INTERESTS_FIVE_MONTHLY_PAYMENTS',
          translate: true,
        },
        { text: fiveMonth.maximumLimit, currency: true },
        { text: fiveMonth.realWithoutInsuranceLimit, currency: true },
        { text: fiveMonth.realWithInsuranceLimit, currency: true },
      ]);
    }

    if (nineMonth) {
      rows.push([
        {
          text:
            'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.WITHOUT_INTERESTS_NINE_MONTHLY_PAYMENTS',
          translate: true,
        },
        { text: nineMonth.maximumLimit, currency: true },
        { text: nineMonth.realWithoutInsuranceLimit, currency: true },
        { text: nineMonth.realWithInsuranceLimit, currency: true },
      ]);
    }

    if (variousCredit) {
      rows.push([
        {
          text: 'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.VARIOUS_CREDIT',
          translate: true,
        },
        { text: variousCredit.maximumLimit, currency: true },
        { text: variousCredit.realWithoutInsuranceLimit, currency: true },
        { text: variousCredit.realWithInsuranceLimit, currency: true },
      ]);
    }

    if (diversalAtention) {
      rows.push([
        {
          text: 'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.DIVERSAL_ATENTION',
          translate: true,
        },
        { text: diversalAtention.maximumLimit, currency: true },
        { text: diversalAtention.realWithoutInsuranceLimit, currency: true },
        { text: diversalAtention.realWithInsuranceLimit, currency: true },
      ]);
    }

    if (socialHousingLoan) {
      rows.push([
        {
          text: 'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.SOCIAL_HOUSING_LOAN_VARIABLE',
          translate: true,
        },
        { text: socialHousingLoan.maximumLimit, currency: true },
        { text: 'N/A', currency: false },
        {
          text: this.simulationWitness
            ? `${this._formatCurrency(socialHousingLoan.realWithInsuranceLimit)} *`
            : socialHousingLoan.realWithInsuranceLimit,
          currency: !this.simulationWitness
        },
      ]);
    }

    if (socialHousingLoanFixed) {
      rows.push([
        {
          text: 'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.SOCIAL_HOUSING_LOAN_FIXED',
          translate: true,
        },
        { text: socialHousingLoanFixed.maximumLimit, currency: true },
        { text: 'N/A', currency: false },
        {
          text: this.simulationWitness
            ? `${this._formatCurrency(socialHousingLoanFixed.realWithInsuranceLimit)} *`
            : socialHousingLoanFixed.realWithInsuranceLimit,
          currency: !this.simulationWitness
        },
      ]);
    }

    if (agreementHousingLoan) {
      rows.push([
        {
          text:
            'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.SOCIAL_HOUSING_AGREEMENT_VARIABLE',
          translate: true,
        },
        { text: agreementHousingLoan.maximumLimit, currency: true },
        { text: 'N/A', currency: false },
        {
          text: this.simulationWitness
            ? `${this._formatCurrency(agreementHousingLoan.realWithInsuranceLimit)} *`
            : agreementHousingLoan.realWithInsuranceLimit,
          currency: !this.simulationWitness
        },
      ]);
    }

    if (agreementHousingLoanFixed) {
      rows.push([
        {
          text:
            'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.SOCIAL_HOUSING_AGREEMENT_FIXED',
          translate: true,
        },
        { text: agreementHousingLoanFixed.maximumLimit, currency: true },
        { text: 'N/A', currency: false },
        {
          text: this.simulationWitness
            ? `${this._formatCurrency(agreementHousingLoanFixed.realWithInsuranceLimit)} *`
            : agreementHousingLoanFixed.realWithInsuranceLimit,
          currency: !this.simulationWitness
        },
      ]);
    }

    return rows;
  }

  _getCreditworthinessOfLoans() {
    const rows = [];
    const {
      fiveMonth,
      nineMonth,
      variousCredit,
      socialHousingLoan,
      agreementHousingLoan,
      socialHousingLoanFixed,
      agreementHousingLoanFixed,
      diversalAtention,
    } = this._rawData;
 
    if (fiveMonth) {
      rows.push({
        loanName:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.WITHOUT_INTERESTS_FIVE_MONTHLY_PAYMENTS',
        maximumLimitLiteral:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT',
        maximumLimit: fiveMonth.maximumLimit,
        realWithoutInsuranceLimitLiteral:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT_WITHOUT_INSURANCE',
        realWithoutInsuranceLimit: fiveMonth.realWithoutInsuranceLimit,
        realWithInsuranceLimitLiteral:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT_WITH_INSURANCE',
        realWithInsuranceLimit: fiveMonth.realWithInsuranceLimit,
        hasAsterisk: false,
      });
    }
 
    if (nineMonth) {
      rows.push({
        loanName:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.WITHOUT_INTERESTS_NINE_MONTHLY_PAYMENTS',
        maximumLimitLiteral:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT',
        maximumLimit: nineMonth.maximumLimit,
        realWithoutInsuranceLimitLiteral:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT_WITHOUT_INSURANCE',
        realWithoutInsuranceLimit: nineMonth.realWithoutInsuranceLimit,
        realWithInsuranceLimitLiteral:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT_WITH_INSURANCE',
        realWithInsuranceLimit: nineMonth.realWithInsuranceLimit,
        hasAsterisk: false,
      });
    }
 
    if (variousCredit) {
      rows.push({
        loanName: 'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.VARIOUS_CREDIT',
        maximumLimitLiteral:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT',
        maximumLimit: variousCredit.maximumLimit,
        realWithoutInsuranceLimitLiteral:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT_WITHOUT_INSURANCE',
        realWithoutInsuranceLimit: variousCredit.realWithoutInsuranceLimit,
        realWithInsuranceLimitLiteral:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT_WITH_INSURANCE',
        realWithInsuranceLimit: variousCredit.realWithInsuranceLimit,
        hasAsterisk: false,
      });
    }
 
    if (diversalAtention) {
      rows.push({
        loanName: 'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.DIVERSAL_ATENTION',
        maximumLimitLiteral:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT',
        maximumLimit: diversalAtention.maximumLimit,
        realWithoutInsuranceLimitLiteral:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT_WITHOUT_INSURANCE',
        realWithoutInsuranceLimit: diversalAtention.realWithoutInsuranceLimit,
        realWithInsuranceLimitLiteral:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT_WITH_INSURANCE',
        realWithInsuranceLimit: diversalAtention.realWithInsuranceLimit,
        hasAsterisk: false,
      });
    }
 
    const formatValueIfZeroNA = (value) => (value === 0 ? 'N/A' : value);
 
    const addHousingRow = (loan, name) => {
      rows.push({
        loanName: name,
        maximumLimitLiteral:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT',
        maximumLimit: formatValueIfZeroNA(loan.maximumLimit),
        realWithoutInsuranceLimitLiteral:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT_WITHOUT_INSURANCE',
        realWithoutInsuranceLimit: formatValueIfZeroNA(loan.realWithoutInsuranceLimit),
        realWithInsuranceLimitLiteral:
          'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.MAX_LIMIT_WITH_INSURANCE',
        realWithInsuranceLimit: loan.realWithInsuranceLimit,
        hasAsterisk: this.simulationWitness,
      });
    };
 
    if (socialHousingLoan) {
      addHousingRow(
        socialHousingLoan,
        'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.SOCIAL_HOUSING_LOAN_VARIABLE'
      );
    }
    if (socialHousingLoanFixed) {
      addHousingRow(
        socialHousingLoanFixed,
        'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.SOCIAL_HOUSING_LOAN_FIXED'
      );
    }
    if (agreementHousingLoan) {
      addHousingRow(
        agreementHousingLoan,
        'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.SOCIAL_HOUSING_AGREEMENT_VARIABLE'
      );
    }
    if (agreementHousingLoanFixed) {
      addHousingRow(
        agreementHousingLoanFixed,
        'LOANS.CREDITWORTHINESS.BY_LOAN_TYPE.TABLE.SOCIAL_HOUSING_AGREEMENT_FIXED'
      );
    }
 
    return rows;
  }
}



// WEBPACK FOOTER //
// ./client/app/loans.module/features/creditworthiness/creditworthiness.formatter.js